import { useEffect } from "react";
import "@/App.css";
import {
    BrowserRouter,
    Routes,
    Route,
    useLocation,
} from "react-router-dom";
import { LanguageProvider } from "@/context/LanguageProvider";
import { SiteDataProvider } from "@/context/SiteDataProvider";
import { AdminAuthProvider } from "@/context/AdminAuthProvider";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { ProtectedRoute } from "@/components/admin/ProtectedRoute";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Practice from "@/pages/Practice";
import Process from "@/pages/Process";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminChangePassword from "@/pages/admin/AdminChangePassword";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminContent from "@/pages/admin/AdminContent";
import AdminPracticeAreas from "@/pages/admin/AdminPracticeAreas";
import AdminFaqs from "@/pages/admin/AdminFaqs";
import AdminBlogList from "@/pages/admin/AdminBlogList";
import AdminBlogEditor from "@/pages/admin/AdminBlogEditor";
import AdminImages from "@/pages/admin/AdminImages";
import AdminContactInfo from "@/pages/admin/AdminContactInfo";
import AdminInbox from "@/pages/admin/AdminInbox";
import AdminAccount from "@/pages/admin/AdminAccount";

function ScrollToTop() {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (hash) {
            requestAnimationFrame(() => {
                const el = document.getElementById(hash.slice(1));
                if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                    return;
                }
                window.scrollTo({ top: 0, behavior: "instant" });
            });
        } else {
            window.scrollTo({ top: 0, behavior: "instant" });
        }
    }, [pathname, hash]);
    return null;
}

function PublicSite() {
    return (
        <SiteDataProvider>
            <PublicLayout>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/a-propos" element={<About />} />
                    <Route path="/domaines" element={<Practice />} />
                    <Route path="/fonctionnement" element={<Process />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/mentions-legales" element={<Legal />} />
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </PublicLayout>
        </SiteDataProvider>
    );
}

function AdminSection() {
    return (
        <AdminAuthProvider>
            <Routes>
                <Route path="login" element={<AdminLogin />} />
                {/* Standalone forced-change route (accessible only when
                    must_change_password is true) */}
                <Route
                    element={<ProtectedRoute allowPasswordChange={true} />}
                >
                    <Route
                        path="change-password"
                        element={<AdminChangePassword />}
                    />
                </Route>
                {/* All other admin pages require full access */}
                <Route element={<ProtectedRoute />}>
                    <Route element={<AdminLayout />}>
                        <Route index element={<AdminDashboard />} />
                        <Route path="contenus" element={<AdminContent />} />
                        <Route
                            path="domaines"
                            element={<AdminPracticeAreas />}
                        />
                        <Route path="faq" element={<AdminFaqs />} />
                        <Route path="blog" element={<AdminBlogList />} />
                        <Route
                            path="blog/nouveau"
                            element={<AdminBlogEditor />}
                        />
                        <Route
                            path="blog/:slug/modifier"
                            element={<AdminBlogEditor />}
                        />
                        <Route path="images" element={<AdminImages />} />
                        <Route
                            path="coordonnees"
                            element={<AdminContactInfo />}
                        />
                        <Route path="messages" element={<AdminInbox />} />
                        <Route path="compte" element={<AdminAccount />} />
                    </Route>
                </Route>
            </Routes>
        </AdminAuthProvider>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <LanguageProvider>
                    <ScrollToTop />
                    <Routes>
                        <Route path="/admin/*" element={<AdminSection />} />
                        <Route path="/*" element={<PublicSite />} />
                    </Routes>
                </LanguageProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
